import { onBeforeUnmount, onMounted, type Ref } from 'vue'

const PENDING_CLASS = 'section-reveal--pending'
const VISIBLE_CLASS = 'section-reveal--visible'
const VIEWPORT_TRIGGER_RATIO = 0.85
const OBSERVER_ROOT_MARGIN = '0px 0px -15% 0px'

let sharedObserver: IntersectionObserver | null = null
let observedCount = 0

const revealElement = (element: HTMLElement): void => {
  const cleanup = (): void => {
    element.removeEventListener('transitionend', onTransitionEnd)
    element.classList.remove(PENDING_CLASS, VISIBLE_CLASS)
  }

  const onTransitionEnd = (event: TransitionEvent): void => {
    const isOwnTransformTransition = event.target === element && event.propertyName === 'transform'
    if (isOwnTransformTransition) cleanup()
  }

  element.addEventListener('transitionend', onTransitionEnd)
  element.classList.add(VISIBLE_CLASS)
}

const unobserveElement = (element: HTMLElement): void => {
  if (!sharedObserver) return

  sharedObserver.unobserve(element)
  observedCount -= 1

  if (observedCount <= 0) {
    sharedObserver.disconnect()
    sharedObserver = null
    observedCount = 0
  }
}

const observeElement = (element: HTMLElement): void => {
  sharedObserver ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue

      unobserveElement(entry.target as HTMLElement)
      revealElement(entry.target as HTMLElement)
    }
  }, { rootMargin: OBSERVER_ROOT_MARGIN, threshold: 0 })

  sharedObserver.observe(element)
  observedCount += 1
}

export const useSectionReveal = (refTarget: Ref<HTMLElement | null>): void => {
  let observedElement: HTMLElement | null = null

  onMounted(() => {
    const element = refTarget.value
    if (!element) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    const alreadyInViewport = element.getBoundingClientRect().top < window.innerHeight * VIEWPORT_TRIGGER_RATIO
    if (alreadyInViewport) return

    element.classList.add(PENDING_CLASS)
    observeElement(element)
    observedElement = element
  })

  onBeforeUnmount(() => {
    if (!observedElement) return

    unobserveElement(observedElement)
    observedElement.classList.remove(PENDING_CLASS, VISIBLE_CLASS)
    observedElement = null
  })
}
